import React, { useEffect, useState } from 'react';
import { Box } from '@indeed/ifl-components';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { usePageFlow } from '../../lib/pageFlow/usePageFlow';

export interface PageTransitionProps {
    children: React.ReactNode;
    pageKey: string;
}

/**
 * React Router stamps a monotonic `idx` onto `window.history.state` for every
 * entry. Remembering the previous entry's index (module scope so it survives the
 * page unmount/remount that happens on every navigation) lets us tell a browser
 * "back" from a browser "forward" — both surface as `NavigationType.Pop`, so the
 * index delta is the only reliable signal (IAFE-383, P5).
 */
let lastHistoryIdx: number | null = null;

const getHistoryIdx = (): number | null => {
    if (typeof window === 'undefined') return null;
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    return typeof idx === 'number' ? idx : null;
};

const PageTransition: React.FunctionComponent<PageTransitionProps> = ({ children, pageKey }) => {
    const [isVisible, setIsVisible] = useState(false);
    const location = useLocation();
    const navigationType = useNavigationType();
    const { allSteps } = usePageFlow();

    const getSlideDirection = (): string => {
        const currentPath = location.pathname;
        const previousPath = location.state?.from || '';

        // A POP is a browser/in-app Back *or* Forward (both report Pop). goBack()
        // now pops too, so this drives both buttons. Use React Router's history
        // index delta to tell them apart: a lower index is backward, a higher one
        // is forward. Falls through to the order-based logic when no index is
        // available (e.g. initial load, MemoryRouter in tests).
        if (navigationType === NavigationType.Pop) {
            const idx = getHistoryIdx();
            if (idx !== null && lastHistoryIdx !== null && idx !== lastHistoryIdx) {
                return idx < lastHistoryIdx ? 'left' : 'right';
            }
        }

        // Handle light-apply contact-info sub-routes (1-5)
        const isCurrentLightApplyContactInfo = currentPath.includes('/light-apply/contact-info/');
        const isPreviousLightApplyContactInfo = previousPath.includes('/light-apply/contact-info/');

        if (isCurrentLightApplyContactInfo && isPreviousLightApplyContactInfo) {
            // Extract step numbers from paths like "/light-apply/contact-info/2"
            const currentStep = parseInt(currentPath.split('/').pop() || '0', 10);
            const previousStep = parseInt(previousPath.split('/').pop() || '0', 10);

            return currentStep > previousStep ? 'right' : 'left';
        }

        // Handle mid-setup-feedback navigation hierarchy:
        //   flow page (e.g. /resume) -> /mid-setup-feedback -> /mid-setup-feedback/:category
        // Going deeper is "forward" (right), going back is "backward" (left).
        const feedbackBase = '/mid-setup-feedback';
        const isCurrentFeedback = currentPath.includes(feedbackBase);
        const isPreviousFeedback = previousPath.includes(feedbackBase);

        if (isCurrentFeedback || isPreviousFeedback) {
            // POP means navigate(-1) or browser back — always backward
            if (navigationType === NavigationType.Pop) return 'left';

            const feedbackDepth = (path: string): number => {
                if (!path || !path.includes(feedbackBase)) return 0;
                const afterBase = path.split(feedbackBase)[1] || '';
                // /mid-setup-feedback = depth 1, /mid-setup-feedback/:category = depth 2
                return afterBase.replace(/^\//, '').length > 0 ? 2 : 1;
            };
            const currentDepth = feedbackDepth(currentPath);
            const previousDepth = feedbackDepth(previousPath);
            return currentDepth > previousDepth ? 'right' : 'left';
        }

        // Use current flow definition to determine page order
        if (!allSteps || allSteps.length === 0) {
            // Fallback: if no flow data available, default to forward direction
            return 'right';
        }

        // Create page order from current flow steps
        const pageOrder = allSteps.map((step) => step.routes[0].replace(/^\//, ''));

        // Clean up paths for comparison
        const currentPathClean = currentPath.replace(/^\//, '') || '';
        const previousPathClean = previousPath.replace(/^\//, '') || '';

        const currentIndex = pageOrder.indexOf(currentPathClean);
        const previousIndex = pageOrder.indexOf(previousPathClean);

        // If we can't determine direction, default to slide from right (forward)
        if (currentIndex === -1 || previousIndex === -1) {
            return 'right';
        }

        // Slide from right when going forward, from left when going backward
        return currentIndex > previousIndex ? 'right' : 'left';
    };

    const slideDirection = getSlideDirection();
    const slideDistance = slideDirection === 'right' ? '30px' : '-30px';

    // Check if we're in light-apply flow to adjust padding
    const isLightApplyFlow = location.pathname.includes('/light-apply/');

    useEffect(() => {
        // Record the index of the entry we just rendered so the next POP can
        // compute its direction from the delta. Runs after the slide direction is
        // computed for this render, so it never clobbers the current comparison.
        const idx = getHistoryIdx();
        if (idx !== null) {
            lastHistoryIdx = idx;
        }
    }, [location.key]);

    useEffect(() => {
        // Reset animation state when page changes
        setIsVisible(false);

        // Scroll to top when page changes (IAFE-399)
        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
            window.scrollTo(0, 0);
        }

        // Use requestAnimationFrame for smoother transitions (reasonable optimization)
        const frame = requestAnimationFrame(() => {
            setIsVisible(true);
        });

        return () => cancelAnimationFrame(frame);
    }, [pageKey]);

    return (
        <Box
            sx={{
                maxInlineSize: isLightApplyFlow ? '100%' : '80rem',
                margin: '0 auto',
                padding: 0,
                inlineSize: '100%',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : `translateX(${slideDistance})`,
                transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
                '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                    transform: 'none',
                },
            }}
        >
            {children}
        </Box>
    );
};

export default PageTransition;
